import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import financialTermsService from '../../services/api/financialTermsService';
import { getPlacements, placementCustomerName } from '../../utils/adminV2';
import PayrollCorrectionReview from './PayrollCorrectionReview';

const decimalHours = (minutes) => (Number(minutes || 0) / 60).toLocaleString(undefined, { maximumFractionDigits: 4 });
const authorityLabel = (value) => value === 'accept_crm' ? 'Use the reviewed CRM salary schedule'
  : value === 'retain_local' ? 'Retain the local rate' : 'Unknown decision';
// Authority is employee-wide and append-only. The history endpoint orders this
// ledger by decisionSequence; it remains available even without accepted terms.
const latestAuthority = (history) => history.legacyAuthorityDecisions?.[0]
  || history.current?.find((publication) => publication.localAuthorityDecision)?.localAuthorityDecision || null;
const decisionTime = (value) => value && Number.isFinite(Date.parse(value))
  ? new Date(value).toLocaleString('en-GB', { timeZone: 'Europe/Copenhagen' }) : 'Time unavailable';
export function allocationMinutes(value) {
  const amount = Number(String(value).replace(',', '.'));
  if (!String(value).trim() || !Number.isFinite(amount) || amount < 0
    || Math.abs(amount * 60 - Math.round(amount * 60)) > 0.00001) throw new Error('Enter hours that resolve to whole minutes, for example 7.5.');
  return Math.round(amount * 60);
}
const combineSegments = (segments, period) => {
  const map = new Map();
  segments.filter((segment) => segment.workDate?.startsWith(period)).forEach((segment) => {
    const key = `${segment.workDate}:${segment.placementId}`;
    const prior = map.get(key);
    map.set(key, { workDate: segment.workDate, placementId: String(segment.placementId), minutes: (prior?.minutes || 0) + segment.minutes });
  });
  return [...map.values()];
};

export default function PayrollFinancialDetails({ row, year, month, disabled, authorityDisabled, onAllocate, onAuthoritySaved, customers }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('terms');
  const [history, setHistory] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [decision, setDecision] = useState('');
  const [savedDecision, setSavedDecision] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveNotice, setSaveNotice] = useState('');
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const [refreshWarning, setRefreshWarning] = useState('');
  const requestRef = useRef(0);
  const closeRef = useRef(null);
  const triggerRef = useRef(null);
  const placements = getPlacements(row);
  const period = `${year}-${String(month).padStart(2, '0')}`;
  const readiness = row.financialReadiness;
  const allocationDisabled = disabled || saving || loading;
  const close = () => {
    if (saving) return;
    requestRef.current += 1;
    setOpen(false); triggerRef.current?.focus();
  };
  const loadHistory = async (request) => {
    const fresh = await financialTermsService.history({ employeeId: row.employeeId, component: 'salary' });
    if (request !== requestRef.current) return;
    const saved = latestAuthority(fresh);
    setHistory(fresh); setSavedDecision(saved); setDecision(saved?.decision || '');
  };
  const show = async () => {
    const request = ++requestRef.current;
    setOpen(true); setTab('terms'); setError(''); setHistory(null); setLoading(true); setReason(''); setDecision('');
    setSavedDecision(null); setSaveNotice(''); setRefreshWarning(''); setNeedsRefresh(false);
    setAllocations((row.financialAllocations || combineSegments(row.workSegments || [], period))
      .map((item) => ({ ...item, hours: String(item.minutes / 60) })));
    setTimeout(() => closeRef.current?.focus(), 0);
    try { await loadHistory(request); }
    catch (err) { if (request === requestRef.current) { setError(err.message); setNeedsRefresh(true); } }
    finally { if (request === requestRef.current) setLoading(false); }
  };
  const refreshDetails = async () => {
    // The parent reload replaces editable payroll rows. Preserve unsaved hours,
    // allowances and allocations until the administrator saves Payroll.
    if (loading || saving || authorityDisabled) return;
    const request = ++requestRef.current;
    setLoading(true); setError(''); setRefreshWarning('');
    try {
      await onAuthoritySaved();
      await loadHistory(request);
      if (request === requestRef.current) { setNeedsRefresh(false); setReason(''); }
    } catch (err) {
      if (request === requestRef.current) { setNeedsRefresh(true); setRefreshWarning(`Could not refresh payroll readiness: ${err.message}`); }
    } finally { if (request === requestRef.current) setLoading(false); }
  };
  const apply = () => {
    if (allocationDisabled) return;
    try {
      const values = allocations.map((item) => ({ workDate: item.workDate, placementId: item.placementId, minutes: allocationMinutes(item.hours) }));
      if (values.some((item) => !item.workDate.startsWith(`${period}-`) || !item.placementId)) throw new Error('Choose a date in this payroll month and an employment for every allocation.');
      onAllocate(values); close();
    } catch (err) { setError(err.message); }
  };
  const decide = async () => {
    if (saving || loading || disabled || authorityDisabled || !history || needsRefresh) return;
    const request = requestRef.current;
    setSaving(true); setError(''); setSaveNotice(''); setRefreshWarning('');
    let saved;
    try {
      saved = await financialTermsService.decideLocalAuthority(row.employeeId, {
        decision, reason, expectedHourlyRateEurCents: history.legacyHourlyRateEurCents,
      });
    } catch (err) {
      // A lost response may follow a committed audit record. Require a read
      // before retrying instead of claiming that a transport failure rolled it back.
      const rejected = err.status >= 400 && err.status < 500;
      setError(`${rejected ? 'Decision was not saved' : 'Could not confirm whether the decision was saved'}: ${err.message}`);
      setNeedsRefresh(true); setSaving(false); return;
    }
    setSavedDecision(saved); setDecision(saved.decision); setReason('');
    setSaveNotice(`Decision saved: ${authorityLabel(saved.decision)}. Payroll readiness is checked separately.`);
    try {
      // Saving and refreshing are separate outcomes. Keep the receipt visible
      // if either subsequent read fails; never silently submit the decision again.
      await onAuthoritySaved();
      await loadHistory(request);
    } catch (err) {
      setNeedsRefresh(true);
      setRefreshWarning(`Your decision was saved, but payroll readiness could not be refreshed: ${err.message}`);
    } finally { setSaving(false); }
  };
  const authorityIsCurrent = savedDecision && history && !needsRefresh
    && savedDecision.existingHourlyRateEurCents != null && history.legacyHourlyRateEurCents != null
    && Number(savedDecision.existingHourlyRateEurCents) === Number(history.legacyHourlyRateEurCents);
  return <div style={{ fontSize: '.72rem', marginTop: '.3rem' }}>
    {readiness?.blockers?.length > 0 && <span style={{ color: '#9a5610' }}>
      {readiness.blockers.length} payroll readiness {readiness.blockers.length === 1 ? 'issue' : 'issues'}{' '}
    </span>}
    <button ref={triggerRef} type="button" onClick={show} aria-label={`Rates and readiness for ${row.name}`}>Rates &amp; readiness</button>
    {open && createPortal(<div style={{ position: 'fixed', inset: 0, zIndex: 1300, background: '#0007', display: 'grid', placeItems: 'center', padding: 16 }}>
      <section role="dialog" aria-modal="true" aria-label={`Rates and payroll readiness — ${row.name}`}
        onKeyDown={(event) => {
          if (event.key === 'Escape') { event.stopPropagation(); close(); }
          if (event.key === 'Tab') {
            const controls = [...event.currentTarget.querySelectorAll('button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),a[href]')];
            if (!event.shiftKey && document.activeElement === controls[controls.length - 1]) { event.preventDefault(); controls[0]?.focus(); }
            if (event.shiftKey && document.activeElement === controls[0]) { event.preventDefault(); controls[controls.length - 1]?.focus(); }
          }
        }} style={{ background: '#fff', borderRadius: 8, padding: 24, width: 'min(1050px, 95vw)', maxHeight: '90vh', overflow: 'auto' }}>
        <button ref={closeRef} type="button" disabled={saving} onClick={close} style={{ float: 'right' }}>Close</button>
        <h2>{row.name} — {period}</h2>
        <p>Review applicable rates and payroll requirements. Saved payments and exports keep their recorded basis.</p>
        {saveNotice && <p role="status" style={{ color: '#17643c' }}>{saveNotice}</p>}
        {refreshWarning && <p role="alert" style={{ color: '#9a5610' }}>{refreshWarning}</p>}
        <button type="button" disabled={loading || saving || authorityDisabled} onClick={refreshDetails}>Refresh details</button>
        {authorityDisabled && <p>Save Payroll before refreshing these details.</p>}
        {readiness?.blockers?.length ? <ul>{readiness.blockers.map((issue, index) => <li key={`${issue.code}-${index}`}>
          <strong>{issue.code}</strong>: {issue.remediation || issue.message} {issue.owner && <small>({issue.owner})</small>}
        </li>)}</ul> : <p>{readiness?.status === 'legacy' ? 'Using the recorded local employee rate; no CRM schedule has been applied.' : 'No rate-calculation blockers reported.'}</p>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" onClick={() => setTab('terms')}>Rates and history</button>
          <button type="button" onClick={() => setTab('allocations')}>Allocate admin hours</button>
          {row.paymentStatus === 'paid' && <button type="button" onClick={() => setTab('correction')}>Review correction</button>}
        </div>
        {error && <p role="alert" style={{ color: '#a22' }}>{error}</p>}
        {tab === 'correction' && <PayrollCorrectionReview employeeId={row.employeeId} year={year} month={month} />}
        {tab === 'terms' && <>
          {loading && <p>Loading rate history…</p>}
          <p>Local hourly rate: {!history ? 'Not loaded' : history.legacyHourlyRateEurCents == null ? 'Not recorded' : `${history.legacyHourlyRateEurCents / 100} EUR`}</p>
          {savedDecision ? <section aria-label="Saved salary authority decision" style={{ border: '1px solid #ccd', padding: 12, margin: '8px 0' }}>
            <strong>Saved decision: {authorityLabel(savedDecision.decision)}</strong>
            <p>Recorded {decisionTime(savedDecision.createdAt)} (Copenhagen time).</p>
            <p>Reviewed local rate: {savedDecision.existingHourlyRateEurCents == null ? 'Not recorded' : `${savedDecision.existingHourlyRateEurCents / 100} EUR`}</p>
            <p>Reason: {savedDecision.reason}</p>
            {needsRefresh ? <p>Refresh the details to verify whether this decision still applies to the current rate.</p>
              : !authorityIsCurrent ? <p>The local rate has changed since this decision. Review the current amount and record a new decision.</p>
                : <p>{savedDecision.decision === 'accept_crm'
                  ? 'CRM is the reviewed salary authority. Missing salary terms, holiday rules or other payroll requirements still need to be resolved.'
                  : 'Local authority is retained. This does not apply the single local rate over dated CRM terms; payroll remains blocked until its dated authority is resolved.'}</p>}
          </section> : history && <p>No salary authority decision has been recorded.</p>}
          {(history?.current || row.financialTerms || []).map((publication) => <div key={publication.id || publication.placementId} style={{ border: '1px solid #ccd', padding: 12, margin: '8px 0' }}>
            <strong>Placement {publication.externalPlacementId || publication.placementId}</strong>
            <p>Applied in Tempus: generation {publication.generation} · Latest received by Tempus: generation {publication.desiredGeneration || publication.generation} · Payroll employer {publication.employerCvr || 'Unresolved'}</p>
            {!(publication.terms || []).length && <p>No salary terms were included in the publication received by Tempus. Check salary readiness and delivery in CRM.</p>}
            {publication.latestRejection && <p>Latest rejected update: {publication.latestRejection.code}. Correct the source and publish a new generation.</p>}
            {publication.blockers?.length > 0 && <ul>{publication.blockers.map((blocker, index) => <li key={index}>
              {blocker.code}: {blocker.remediation || blocker.message}
            </li>)}</ul>}
            {(publication.terms || []).map((term) => <p key={term.revisionId}>
              {term.kind}: {term.amount ?? term.calculationRule?.method} {term.currency} / {term.unit};{' '}
              {term.effectiveFrom} to {term.effectiveToExclusive ? `${term.effectiveToExclusive} (exclusive)` : 'open-ended'}<br />
              <small>{term.authority} · Revision {term.revisionId} · Source {term.sourceRevisionId}</small>
            </p>)}
          </div>)}
          {(history?.current || row.financialTerms || []).length > 0 && <p>Newer changes queued in CRM are not visible here until delivered. Recording an authority decision does not publish missing salary or holiday terms.</p>}
          {history?.history?.length > 0 && <details><summary>Publication history ({history.history.length})</summary>
            <ul>{history.history.map((publication) => <li key={publication.id}>
              Generation {publication.generation} · {publication.appliedAt} · {publication.externalPlacementId} · Revisions {(publication.terms || []).map((term) => term.revisionId).join(', ')}
            </li>)}</ul>
          </details>}
          {authorityDisabled && <p>Save Payroll before recording an authority decision.</p>}
          {history?.current?.length > 0 && history.legacyHourlyRateEurCents != null && <fieldset disabled={loading || saving || needsRefresh || disabled || authorityDisabled}>
            <legend>Review the existing local rate</legend>
            <p>An imported or manually entered rate is not replaced automatically. Record the authority decision and its source.</p>
            <p>Retaining the local rate preserves its authority. CRM calculations remain blocked until the dated local authority is resolved.</p>
            <p>This decision is saved with Record decision. The main Payroll Save button does not save this form. Enter a review reason of at least 10 characters.</p>
            <label>Decision <select value={decision} onChange={(event) => setDecision(event.target.value)}>
              <option value="">Choose a decision</option><option value="accept_crm">Use the reviewed CRM salary schedule</option><option value="retain_local">Retain the local rate</option>
            </select></label>{' '}
            <label>Reason <textarea value={reason} onChange={(event) => setReason(event.target.value)} minLength={10} maxLength={1000} /></label>
            <button type="button" disabled={!decision || reason.trim().length < 10} onClick={decide}>Record decision</button>
          </fieldset>}
        </>}
        {tab === 'allocations' && <>
          <p>Allocate this month’s admin hours to the work date and employment. Carried hours retain their own recorded basis. Save Payroll after applying these allocations.</p>
          <p>Admin hours: {row.adminHours ?? row.hours ?? 0} · Allocated: {decimalHours(allocations.reduce((total, item) => total + (Number(item.hours.replace(',', '.')) || 0) * 60, 0))} h</p>
          <table style={{ width: '100%' }}><thead><tr><th>Work date</th><th>Employment</th><th>Hours</th><th /></tr></thead><tbody>
            {allocations.map((item, index) => <tr key={index}>
              <td><input aria-label={`Work date ${index + 1}`} type="date" min={`${period}-01`} value={item.workDate} disabled={allocationDisabled}
                onChange={(event) => setAllocations((items) => items.map((value, i) => i === index ? { ...value, workDate: event.target.value } : value))} /></td>
              <td><select aria-label={`Employment ${index + 1}`} value={item.placementId} disabled={allocationDisabled}
                onChange={(event) => setAllocations((items) => items.map((value, i) => i === index ? { ...value, placementId: event.target.value } : value))}>
                <option value="">Choose employment</option>{placements.map((placement) => <option key={placement.id} value={placement.id}>{placementCustomerName(placement, customers)} ({placement.startDate}–{placement.endDate || 'open'})</option>)}
              </select></td>
              <td><input aria-label={`Hours ${index + 1}`} inputMode="decimal" value={item.hours} disabled={allocationDisabled}
                onChange={(event) => setAllocations((items) => items.map((value, i) => i === index ? { ...value, hours: event.target.value } : value))} /></td>
              <td><button type="button" disabled={allocationDisabled} onClick={() => setAllocations((items) => items.filter((_, i) => i !== index))}>Remove</button></td>
            </tr>)}
          </tbody></table>
          <button type="button" disabled={allocationDisabled} onClick={() => setAllocations((items) => [...items, { workDate: `${period}-01`, placementId: '', hours: '' }])}>Add allocation</button>{' '}
          <button type="button" disabled={allocationDisabled} onClick={apply}>Apply allocations</button>{' '}
          <button type="button" disabled={allocationDisabled} onClick={() => { onAllocate(null); close(); }}>Use reported work dates</button>
        </>}
      </section>
    </div>, document.body)}
  </div>;
}
